import { MutableRefObject, useEffect, useState } from 'react'

type TUseIntersection = (
	element: MutableRefObject<HTMLElement>,
	options?: Pick<IntersectionObserverInit, `rootMargin` | `threshold`>,
	once?: boolean
) => boolean

/**
 * A hook that allows you to track the appearance of a component in the viewport
 * @param element - Ref on the element whose appearance needs to be track
 * @param rootMargin - Margin around the root. Can have values similar to the CSS margin property, e.g. "10px 20px 30px 40px" (top, right, bottom, left). The values can be percentages. This set of values serves to grow or shrink each side of the root element's bounding box before computing intersections. Defaults to all zeros.
 * @param threshold - Either a single number or an array of numbers which indicate at what percentage of the target's visibility the observer's callback should be executed. If you only want to detect when visibility passes the 50% mark, you can use a value of 0.5. If you want the callback to run every time visibility passes another 25%, you would specify the array [0, 0.25, 0.5, 0.75, 1]. The default is 0 (meaning as soon as even one pixel is visible, the callback will be run). A value of 1.0 means that the threshold isn't considered passed until every pixel is visible.
 * @param once – Delete the observer after one intersection
 *
 * @see https://developer.mozilla.org/en-US/docs/Web/API/Intersection_Observer_API
 *
 * @example
 * const App = () => {
 *   const ref = useRef();
 *   const inViewport = useIntersection(ref, { rootMargin: '0px'}); // Trigger as soon as the element becomes visible
 *   const inViewport = useIntersection(ref, { rootMargin: '-200px'}); // Trigger if 200px is visible from the element
 *
 *   if (inViewport) {
 *       console.log('in viewport:', ref.current);
 *   }
 *
 *   return (
 *       <React.Fragment>
 *           <Header />
 *           <Content />
 *           <Footer ref={ref} />
 *       </React.Fragment>
 *   );
 * }`
 */
export const useIntersection: TUseIntersection = (element, { rootMargin = `0px`, threshold = 0 }, once = false) => {
	const [isVisible, setState] = useState<boolean>(false)

	useEffect(() => {
		const observer = new IntersectionObserver(
			([entry]) => {
				setState(entry.isIntersecting)

				if (entry.isIntersecting && once) {
					observer.unobserve(element.current)
				}
			},
			{ rootMargin, threshold }
		)

		if (element.current) observer.observe(element.current)

		if (!once) {
			return () => element.current && observer.unobserve(element.current)
		}
	}, [element, rootMargin, threshold, once])

	return isVisible
}
